import { getApiUrl } from '../config/api';

export interface VideoUploadResponse {
  projectId: string;
  videoUrl?: string; // Optional since it might be in the job processing
  duration?: number;
  jobId?: string; // For async processing
  message?: string;
  isSegmented?: boolean; // NEW: For segmented video uploads
  totalSegments?: number; // NEW: Number of segments created
  metadata?: { // For compatibility
    duration: number;
    width: number;
    height: number;
  };
}

/**
 * Upload video file with audio to create a new project
 * Uses the existing caption video upload endpoint with no_caption flag
 * The video will be added as the primary media item automatically by backend
 */
export const uploadVideoWithAudio = async (
  videoFile: File,
  onProgress?: (progress: number) => void
): Promise<VideoUploadResponse> => {
  const formData = new FormData();
  formData.append('file', videoFile);
  formData.append('no_caption', 'true'); // NEW: Tell backend this is for CreateContent (no captions needed)

  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Use existing endpoint that already handles video uploads
  const response = await fetch(
    getApiUrl('/video/upload-video-for-caption-with-progress'),
    {
      method: 'POST',
      headers,
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Video upload failed');
  }

  const result = await response.json();
  return result;
};

/**
 * Get video metadata (duration, dimensions) from video file
 */
export const getVideoMetadata = (videoFile: File): Promise<{
  duration: number;
  width: number;
  height: number;
}> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight
      });
      
      // Clean up
      URL.revokeObjectURL(video.src);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video metadata'));
      URL.revokeObjectURL(video.src);
    };

    video.src = URL.createObjectURL(videoFile);
  });
};
